<template>
<div class="page-container" :style="backgroundStyle">
    <!-- 动态粒子背景 -->
    <div class="particles">
        <div v-for="n in 30" :key="n" class="particle" :style="getParticleStyle()"></div>
    </div>

    <!-- 渐变遮罩层 -->
    <div class="gradient-overlay"></div>

    <!-- 主内容区域 -->
    <div class="content-wrapper">
        <router-link to="/admin" class="back-button">
            <LucideIcon name="arrow-left" :size="18" />
            <span>返回用户管理</span>
        </router-link>

        <div class="glass-card" :class="{ 'show': isLoaded }">
            <div class="page-header">
                <div class="icon-wrapper">
                    <LucideIcon name="filter" :size="28" color="#fff" />
                </div>
                <h1 class="title">分流规则配置</h1>
                <p class="subtitle">全局一套分流策略，所有用户共用；节点仍按用户各自替换</p>
            </div>

            <div class="divider">
                <div class="divider-line"></div>
                <div class="divider-dot"></div>
                <div class="divider-line"></div>
            </div>

            <!-- 加载中 -->
            <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <span>加载中...</span>
            </div>

            <!-- 未登录：引导去管理页登录，本页不再做一套登录表单 -->
            <div v-else-if="!authenticated" class="auth-required">
                <LucideIcon name="lock" :size="42" color="var(--color-accent)" />
                <p class="auth-title">需要管理员登录</p>
                <p class="auth-desc">请先在用户管理页登录，再回到本页配置分流规则</p>
                <router-link to="/admin" class="add-btn auth-btn">前往登录</router-link>
            </div>

            <template v-else>
                <div class="section-block">
                    <div class="section-head">
                        <div class="section-label">
                            <LucideIcon name="plus" :size="14" /> 手动规则
                            <span class="count-badge">{{ config.customRules.length }}</span>
                        </div>
                        <span class="section-note">优先于规则集匹配</span>
                    </div>
                    <p class="custom-rule-tip">填写域名或关键词即可自定义分流，不需要添加规则集。多条手动规则按列表顺序匹配，保存后生效。</p>
                    <form class="custom-rule-form" @submit.prevent="addCustomRule">
                        <label class="custom-field">
                            <span class="field-tag">匹配规则</span>
                            <select v-model="customRuleDraft.type" class="mini-select">
                                <option v-for="type in customRuleTypes" :key="type" :value="type">{{ type }}</option>
                            </select>
                        </label>
                        <label class="custom-field custom-value">
                            <span class="field-tag">{{ customRuleDraft.type === 'DOMAIN-KEYWORD' ? '关键词' : '域名' }}</span>
                            <input v-model.trim="customRuleDraft.value" class="mini-input" type="text" maxlength="253" required :placeholder="customRuleDraft.type === 'DOMAIN-KEYWORD' ? '例如 example' : '例如 example.com，不含 https://'" />
                        </label>
                        <label class="custom-field">
                            <span class="field-tag">策略</span>
                            <select v-model="customRuleDraft.target" class="mini-select">
                                <option v-for="opt in customTargetOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                            </select>
                        </label>
                        <button class="pick-btn" type="submit" :disabled="config.customRules.length >= 300"><LucideIcon name="plus" :size="12" /> 添加规则</button>
                    </form>
                    <p class="custom-rule-tip">DOMAIN 精确匹配域名；DOMAIN-SUFFIX 匹配域名及其子域名；DOMAIN-KEYWORD 匹配域名中包含的关键词。</p>
                    <div v-if="config.customRules.length" class="rule-list">
                        <div v-for="(item, index) in config.customRules" :key="index" class="rule-row custom-rule-row">
                            <span class="rule-index">{{ index + 1 }}</span>
                            <div class="rule-main">
                                <div class="custom-rule-fields">
                                    <select v-model="item.type" class="mini-select" :aria-label="`手动规则 ${index + 1} 匹配类型`">
                                        <option v-for="type in customRuleTypes" :key="type" :value="type">{{ type }}</option>
                                    </select>
                                    <input v-model.trim="item.value" class="mini-input" type="text" maxlength="253" :aria-label="`手动规则 ${index + 1} 域名或关键词`" />
                                    <select v-model="item.target" class="mini-select" :aria-label="`手动规则 ${index + 1} 策略`">
                                        <option v-for="opt in customTargetOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                                    </select>
                                </div>
                                <div class="rule-preview-line">{{ item.type }},{{ item.value }},{{ customRuleTarget(item.target) }}</div>
                            </div>
                            <button type="button" class="icon-btn icon-danger" :aria-label="`删除手动规则 ${index + 1}`" title="删除手动规则" @click="config.customRules.splice(index, 1)">
                                <LucideIcon name="trash-2" :size="14" />
                            </button>
                        </div>
                    </div>
                    <p v-else class="custom-rule-tip">暂未添加手动规则。</p>
                </div>

                <!-- ===== 一、已选规则集 ===== -->
                <div class="section-block">
                    <div class="section-head">
                        <div class="section-label">
                            <LucideIcon name="list-ordered" :size="14" /> 已选规则集
                            <span class="count-badge">{{ ruleSets.length }}</span>
                        </div>
                        <span class="section-note">按名称 A–Z 排序 · 序号为匹配优先级</span>
                    </div>
                    <div class="priority-tip">
                        <LucideIcon name="lightbulb" :size="15" color="rgba(255, 193, 7, 0.9)" />
                        <span>列表按名称 A–Z 展示，数字表示实际匹配优先级。客户端代理组的顺序可在下方「策略组展示顺序」中调整，不影响规则匹配。</span>
                    </div>

                    <div v-if="ruleSets.length === 0" class="empty-state">
                        <LucideIcon name="layers" :size="40" color="var(--color-muted)" />
                        <p>还没有勾选任何规则集，去下方目录里添加</p>
                    </div>

                    <div v-else class="rule-list">
                        <div v-for="item in sortedRuleSets" :key="item.dir" class="rule-row" :class="{ 'row-off': item.enabled === false }">
                            <span class="rule-index" title="实际匹配优先级">{{ ruleSets.indexOf(item) + 1 }}</span>

                            <div class="rule-main">
                                <div class="rule-dir-line">
                                    <span class="rule-dir">{{ item.dir }}</span>
                                    <select
                                        v-if="filesOf(item.dir).length > 1"
                                        v-model="item.file"
                                        class="mini-select file-select"
                                        title="规则集文件（同一目录下的不同变体）"
                                    >
                                        <option v-for="f in filesOf(item.dir)" :key="f" :value="f">{{ f }}</option>
                                    </select>
                                    <span v-else class="rule-file">{{ item.file }}</span>
                                </div>

                                <div class="rule-fields">
                                    <label class="field-inline">
                                        <span class="field-tag">策略组</span>
                                        <input
                                            v-model="item.group"
                                            type="text"
                                            class="mini-input"
                                            maxlength="40"
                                            :placeholder="needGroup(item.target) ? '策略组名' : '该模式不建组'"
                                            :disabled="!needGroup(item.target) && item.target !== 'group'"
                                            list="rule-group-options"
                                        />
                                    </label>
                                    <label class="field-inline">
                                        <span class="field-tag">分流去向</span>
                                        <select v-model="item.target" class="mini-select" @change="onTargetChange(item)">
                                            <option v-for="opt in targetOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                                        </select>
                                    </label>
                                </div>
                                <div class="rule-preview-line">{{ describeRule(item) }}</div>
                            </div>

                            <div class="rule-ops">
                                <button type="button" class="icon-btn icon-danger" title="移除该规则集" @click="removeRuleSet(ruleSets.indexOf(item))">
                                    <LucideIcon name="trash-2" :size="14" />
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 策略组名候选（target=指向已有组 时用得上） -->
                    <datalist id="rule-group-options">
                        <option v-for="name in groupNameOptions" :key="name" :value="name"></option>
                    </datalist>
                </div>

                <div class="section-block">
                    <div class="section-head">
                        <div class="section-label"><LucideIcon name="list-ordered" :size="14" /> 策略组展示顺序</div>
                        <button type="button" class="ghost-btn" @click="config.proxyGroupOrder = []">恢复策略组 A–Z 排序</button>
                    </div>
                    <p class="custom-rule-tip">下方顺序对应客户端代理组列表，包含内置策略组。可以将任意组置底，保存后生效；规则匹配顺序不受影响。</p>
                    <div class="rule-list">
                        <div v-for="(name, index) in sortedProxyGroups" :key="name" class="rule-row">
                            <span class="rule-index">{{ index + 1 }}</span>
                            <div class="rule-main"><span class="rule-dir">{{ name }}</span></div>
                            <div class="rule-ops">
                                <button type="button" class="icon-btn" :disabled="index === 0" title="上移策略组" aria-label="上移策略组" @click="moveProxyGroup(index, index - 1)"><LucideIcon name="arrow-up" :size="14" /></button>
                                <button type="button" class="icon-btn" :disabled="index === sortedProxyGroups.length - 1" title="下移策略组" aria-label="下移策略组" @click="moveProxyGroup(index, index + 1)"><LucideIcon name="arrow-down" :size="14" /></button>
                                <button type="button" class="pick-btn" :disabled="index === 0" @click="moveProxyGroup(index, 0)">置顶</button>
                                <button type="button" class="pick-btn" :disabled="index === sortedProxyGroups.length - 1" @click="moveProxyGroup(index, sortedProxyGroups.length - 1)">置底</button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ===== 二、规则集目录 ===== -->
                <div class="section-block">
                    <div class="section-head">
                        <div class="section-label">
                            <LucideIcon name="layers" :size="14" /> 规则集目录
                            <span class="count-badge">{{ catalog.total || catalogItems.length }}</span>
                        </div>
                        <span class="section-note">blackmatrix7 / ios_rule_script · A–Z</span>
                    </div>

                    <div class="catalog-toolbar">
                        <div class="search-box">
                            <LucideIcon name="search" :size="15" color="rgba(255,255,255,0.45)" />
                            <input
                                v-model="search"
                                type="text"
                                class="search-input"
                                placeholder="搜索目录名，如 Claude / YouTube / China"
                            />
                            <button v-if="search" type="button" class="search-clear" title="清空搜索" @click="search = ''">×</button>
                        </div>
                        <label class="switch-line">
                            <input v-model="onlyUnselected" type="checkbox" />
                            <span class="switch-track"><span class="switch-thumb"></span></span>
                            <span class="switch-text">只看未选</span>
                        </label>
                    </div>

                    <div v-if="catalogLoading" class="logs-loading">
                        <div class="spinner-sm"></div>
                        <span>目录加载中...</span>
                    </div>
                    <div v-else-if="filteredCatalog.length === 0" class="empty-state">
                        <LucideIcon name="search" :size="36" color="var(--color-muted)" />
                        <p>没有匹配的规则集目录</p>
                    </div>
                    <div v-else class="catalog-list">
                        <div v-for="item in visibleCatalog" :key="item.dir" class="catalog-row" :class="{ 'is-picked': isPicked(item.dir) }">
                            <div class="catalog-info">
                                <span class="catalog-dir">{{ item.dir }}</span>
                                <span class="behavior-tag" :class="'behavior-' + item.behavior">{{ item.behavior }}</span>
                                <span v-if="item.files && item.files.length > 1" class="files-tag">{{ item.files.length }} 个文件</span>
                            </div>
                            <button
                                type="button"
                                class="pick-btn"
                                :disabled="isPicked(item.dir)"
                                @click="addRuleSet(item)"
                            >
                                <LucideIcon v-if="!isPicked(item.dir)" name="plus" :size="12" />
                                <LucideIcon v-else name="check-circle" :size="12" />
                                <span>{{ isPicked(item.dir) ? '已选' : '添加' }}</span>
                            </button>
                        </div>
                    </div>

                    <div v-if="hiddenCount > 0" class="catalog-hint">
                        <LucideIcon name="search" :size="13" />
                        <span v-if="!keyword">目录共 {{ filteredCatalog.length }} 条，当前只渲染前 {{ renderLimit }} 条，输入关键词继续筛选</span>
                        <span v-else>匹配 {{ filteredCatalog.length }} 条，仅渲染前 {{ renderLimit }} 条，请继续细化关键词</span>
                    </div>
                </div>

                <!-- ===== 三、基础设置 ===== -->
                <div class="section-block">
                    <div class="section-head">
                        <div class="section-label"><LucideIcon name="sliders-horizontal" :size="14" /> 基础设置</div>
                        <span class="section-note">写进合成配置的内核参数</span>
                    </div>

                    <div class="settings-grid">
                        <label class="field-block full">
                            <span class="field-tag">节点排除正则（exclude-filter）</span>
                            <input v-model="config.excludeFilter" type="text" class="input-field mono" placeholder="(?i)剩余|到期|流量" />
                            <span class="field-hint">机场信息节点不进策略组，(?i) 是 Clash 的忽略大小写写法</span>
                        </label>

                        <label class="field-block full">
                            <span class="field-tag">延迟测试地址（test-url）</span>
                            <input v-model="config.testUrl" type="text" class="input-field mono" />
                        </label>

                        <label class="field-block">
                            <span class="field-tag">混合端口 mixed-port</span>
                            <input v-model.number="config.base.mixedPort" type="number" min="1" max="65535" class="input-field" />
                        </label>

                        <label class="field-block">
                            <span class="field-tag">自动测速间隔（秒）</span>
                            <input v-model.number="config.urlTestInterval" type="number" min="60" class="input-field" />
                        </label>

                        <label class="field-block">
                            <span class="field-tag">测速容差（ms）</span>
                            <input v-model.number="config.urlTestTolerance" type="number" min="0" class="input-field" />
                        </label>

                        <div class="field-block">
                            <span class="field-tag">DNS 模式 enhanced-mode</span>
                            <div class="seg-group">
                                <button
                                    type="button"
                                    class="seg-item"
                                    :class="{ active: config.base.enhancedMode === 'fake-ip' }"
                                    @click="config.base.enhancedMode = 'fake-ip'"
                                >fake-ip</button>
                                <button
                                    type="button"
                                    class="seg-item"
                                    :class="{ active: config.base.enhancedMode === 'redir-host' }"
                                    @click="config.base.enhancedMode = 'redir-host'"
                                >redir-host</button>
                            </div>
                        </div>
                    </div>

                    <div class="toggle-grid">
                        <label class="switch-line"><input v-model="config.base.ipv6" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">IPv6</span></label>
                        <label class="switch-line"><input v-model="config.base.sniffer" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">域名嗅探 sniffer</span></label>
                        <label class="switch-line"><input v-model="config.base.unifiedDelay" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">统一延迟 unified-delay</span></label>
                        <label class="switch-line"><input v-model="config.base.storeSelected" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">记住手选节点</span></label>
                        <label class="switch-line"><input v-model="config.base.allowLan" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">允许局域网连接</span></label>
                    </div>

                    <div class="sub-label">兜底规则（追加在所有规则集之后）</div>
                    <div class="toggle-grid">
                        <label class="switch-line"><input v-model="config.tail.cnSuffixDirect" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">DOMAIN-SUFFIX,cn,DIRECT</span></label>
                        <label class="switch-line"><input v-model="config.tail.geoipLanDirect" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">GEOIP,LAN,DIRECT</span></label>
                        <label class="switch-line"><input v-model="config.tail.geoipCnDirect" type="checkbox" /><span class="switch-track"><span class="switch-thumb"></span></span><span class="switch-text">GEOIP,CN,DIRECT</span></label>
                        <label class="field-inline final-target">
                            <span class="field-tag">漏网之鱼 MATCH</span>
                            <select v-model="config.tail.finalTarget" class="mini-select">
                                <option value="Others">Others</option>
                                <option value="Proxies">Proxies</option>
                                <option value="DIRECT">DIRECT</option>
                            </select>
                        </label>
                    </div>
                </div>

                <!-- ===== 底部操作条 ===== -->
                <div class="action-bar">
                    <button class="add-btn" :disabled="saving" @click="saveConfig">
                        <LucideIcon name="save" :size="15" />
                        <span>{{ saving ? '保存中...' : '保存配置' }}</span>
                    </button>
                    <button class="ghost-btn" :disabled="previewing" @click="openPreview">
                        <LucideIcon name="eye" :size="15" />
                        <span>{{ previewing ? '合成中...' : '预览合成结果' }}</span>
                    </button>
                    <button class="ghost-btn" :disabled="refreshing" @click="refreshCatalog">
                        <LucideIcon name="refresh-cw" :size="15" :class="{ spinning: refreshing }" />
                        <span>{{ refreshing ? '刷新中...' : '刷新规则目录' }}</span>
                    </button>
                </div>

                <div class="footer-tip">
                    <LucideIcon name="lightbulb" :size="16" color="rgba(255, 193, 7, 0.9)" />
                    <span>保存后所有用户的订阅立即生效，客户端下次更新订阅即可拿到新分流</span>
                </div>
            </template>
        </div>
    </div>

    <!-- 预览弹层 -->
    <transition name="modal">
        <div v-if="previewVisible" class="modal-overlay" @click.self="previewVisible = false">
            <div class="preview-modal">
                <div class="preview-header">
                    <div>
                        <div class="preview-title">合成结果预览</div>
                        <div class="preview-subtitle">{{ previewUserLabel }}</div>
                    </div>
                    <button type="button" class="preview-close" @click="previewVisible = false">×</button>
                </div>

                <div class="preview-stats">
                    <span class="stat-chip">节点 {{ previewStats.nodeCount }}</span>
                    <span class="stat-chip">策略组 {{ previewStats.groupCount }}</span>
                    <span class="stat-chip">规则集 {{ previewStats.providerCount }}</span>
                    <span class="stat-chip">规则 {{ previewStats.ruleCount }}</span>
                    <span class="stat-chip">{{ formatBytes(previewStats.bytes) }}</span>
                </div>

                <pre class="preview-pre">{{ previewText }}</pre>
                <div v-if="previewTruncated" class="preview-more">仅展示前 {{ previewMaxLines }} 行，完整内容可复制后查看</div>

                <div class="preview-footer">
                    <label class="field-inline">
                        <span class="field-tag">预览用户</span>
                        <select v-model="previewUserId" class="mini-select" @change="fetchPreview">
                            <option value="">全局默认节点池</option>
                            <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
                        </select>
                    </label>
                    <button type="button" class="ghost-btn" @click="copyPreview">
                        <LucideIcon name="copy" :size="14" />
                        <span>复制完整 YAML</span>
                    </button>
                </div>
            </div>
        </div>
    </transition>

    <!-- Toast 提示 -->
    <transition name="toast">
        <div v-if="showToast" class="toast" :class="toastType">
            <LucideIcon :name="toastType === 'success' ? 'check-circle' : 'x-circle'" :size="18" />
            <span>{{ toastMessage }}</span>
        </div>
    </transition>
</div>
</template>

<script>
import backgroundMixin from '@/mixins/background'
import { sortForDisplay, normalizeDisplayOrder, proxyGroupNames } from '../../shared/rule-display-order'

// 目录一共 666 条，全量渲染会明显卡顿，未搜索时只渲染前 100 条
const CATALOG_RENDER_LIMIT = 100;
// 预览弹层最多展示的行数
const PREVIEW_MAX_LINES = 400;

// 与 server/rule-config.js 的 DEFAULT_RULE_CONFIG 对齐的前端兜底骨架
// 只用于补齐后端没返回的字段，保证 v-model 有响应式的落点
const CONFIG_SKELETON = {
    version: 1,
    excludeFilter: '(?i)剩余|到期|流量|expire|traffic|官网|订阅|重置',
    testUrl: 'http://cp.cloudflare.com/generate_204',
    urlTestInterval: 1800,
    urlTestTolerance: 50,
    base: {
        mixedPort: 7890,
        allowLan: true,
        ipv6: true,
        mode: 'rule',
        logLevel: 'info',
        unifiedDelay: true,
        externalController: '',
        secret: '',
        enhancedMode: 'fake-ip',
        sniffer: true,
        storeSelected: true
    },
    tail: {
        geoipLanDirect: true,
        geoipCnDirect: true,
        cnSuffixDirect: true,
        finalTarget: 'Others'
    },
    ruleSets: [],
    customRules: [],
    proxyGroupOrder: []
};

// target 语义见实施契约 B 节
const TARGET_OPTIONS = [
    { value: 'proxy', label: '走代理（建同名组）' },
    { value: 'direct', label: '走直连（建同名组）' },
    { value: 'direct-only', label: '强制直连（不建组）' },
    { value: 'reject', label: '拦截（不建组，走 AdBlock）' },
    { value: 'group', label: '指向已有策略组' }
];

// 内置保留组名，供 target=group 时选择
const RESERVED_GROUP_NAMES = ['Proxies', 'Auto - UrlTest', 'AdBlock', 'Others', 'DIRECT', 'REJECT'];

export default {
    name: 'RuleSetManager',
    mixins: [backgroundMixin],
    data() {
        return {
            loading: true,
            authenticated: false,
            catalogLoading: false,
            saving: false,
            previewing: false,
            refreshing: false,
            catalog: { total: 0, items: [] },
            config: JSON.parse(JSON.stringify(CONFIG_SKELETON)),
            search: '',
            onlyUnselected: false,
            renderLimit: CATALOG_RENDER_LIMIT,
            previewMaxLines: PREVIEW_MAX_LINES,
            targetOptions: TARGET_OPTIONS,
            customRuleTypes: ['DOMAIN', 'DOMAIN-SUFFIX', 'DOMAIN-KEYWORD'],
            customTargetOptions: [
                { value: 'direct', label: '直连' },
                { value: 'proxy', label: '代理' },
                { value: 'reject', label: '屏蔽' }
            ],
            customRuleDraft: { type: 'DOMAIN', value: '', target: 'direct' },
            // 预览
            previewVisible: false,
            previewYaml: '',
            previewStats: { nodeCount: 0, groupCount: 0, providerCount: 0, ruleCount: 0, bytes: 0 },
            previewUserId: '',
            users: [],
            // Toast
            showToast: false,
            toastMessage: '',
            toastType: 'success'
        }
    },
    computed: {
        ruleSets() {
            return this.config.ruleSets;
        },
        sortedRuleSets() {
            return sortForDisplay(this.ruleSets, [], item => item.dir);
        },
        sortedProxyGroups() {
            return sortForDisplay(proxyGroupNames(this.config), this.config.proxyGroupOrder);
        },
        catalogItems() {
            return (this.catalog.items || []).slice().sort((a, b) => a.dir.localeCompare(b.dir, 'en', { sensitivity: 'base' }));
        },
        keyword() {
            return this.search.trim().toLowerCase();
        },
        // dir -> 目录项的索引，避免每行都在 666 条里线性查找
        catalogByDir() {
            const map = Object.create(null);
            this.catalogItems.forEach(item => { map[item.dir] = item; });
            return map;
        },
        // 已选 dir 的哈希表，避免在列表里做 O(n) 查找
        pickedDirs() {
            const map = Object.create(null);
            this.ruleSets.forEach(item => { map[item.dir] = true; });
            return map;
        },
        filteredCatalog() {
            const kw = this.keyword;
            const onlyUnselected = this.onlyUnselected;
            if (!kw && !onlyUnselected) return this.catalogItems;
            return this.catalogItems.filter(item => {
                if (onlyUnselected && this.pickedDirs[item.dir]) return false;
                if (!kw) return true;
                return item.dir.toLowerCase().indexOf(kw) !== -1;
            });
        },
        // 真正渲染的条数，超出部分靠搜索收敛
        visibleCatalog() {
            return this.filteredCatalog.slice(0, this.renderLimit);
        },
        hiddenCount() {
            return Math.max(0, this.filteredCatalog.length - this.renderLimit);
        },
        // target=group 时可以指向的组名：保留组 + 前面已经会生成的同名组
        groupNameOptions() {
            const names = RESERVED_GROUP_NAMES.slice();
            this.ruleSets.forEach(item => {
                if ((item.target === 'proxy' || item.target === 'direct') && item.group && names.indexOf(item.group) === -1) {
                    names.push(item.group);
                }
            });
            return names;
        },
        previewLines() {
            return this.previewYaml ? this.previewYaml.split('\n') : [];
        },
        previewText() {
            return this.previewLines.slice(0, PREVIEW_MAX_LINES).join('\n');
        },
        previewTruncated() {
            return this.previewLines.length > PREVIEW_MAX_LINES;
        },
        previewUserLabel() {
            if (!this.previewUserId) return '全局默认节点池';
            const hit = this.users.find(u => u.id === this.previewUserId);
            return hit ? `用户：${hit.name}` : '全局默认节点池';
        }
    },
    methods: {
        // 鉴权检查：未登录不做表单，直接引导去 /admin
        async checkAuth() {
            this.loading = true;
            try {
                const res = await this.$axios.get('/api/auth/check');
                this.authenticated = !!res.data.authenticated;
            } catch (err) {
                this.authenticated = false;
            }
            if (this.authenticated) {
                await Promise.all([this.fetchCatalog(), this.fetchConfig(), this.fetchUsers()]);
            }
            this.loading = false;
        },
        // 拉规则集目录（后端已算好 defaultFile / behavior）
        async fetchCatalog() {
            this.catalogLoading = true;
            try {
                const res = await this.$axios.get('/api/rules/catalog');
                this.catalog = {
                    total: res.data.total || (res.data.items || []).length,
                    items: res.data.items || []
                };
            } catch (err) {
                console.error('获取规则集目录失败:', err);
                this.showToastMessage('获取规则集目录失败', 'error');
            } finally {
                this.catalogLoading = false;
            }
        },
        // 拉分流配置
        async fetchConfig() {
            try {
                const res = await this.$axios.get('/api/rules/config');
                this.config = this.hydrateConfig(res.data);
            } catch (err) {
                console.error('获取分流配置失败:', err);
                this.showToastMessage('获取分流配置失败', 'error');
            }
        },
        // 预览时选用户用
        async fetchUsers() {
            try {
                const res = await this.$axios.get('/api/users');
                this.users = Array.isArray(res.data) ? res.data : [];
            } catch (err) {
                this.users = [];
            }
        },
        // 补齐缺失字段，保证 v-model 落点存在；未在页面暴露的字段（externalController / secret / version）原样保留
        hydrateConfig(raw) {
            const source = raw && typeof raw === 'object' ? raw : {};
            const ruleSets = Array.isArray(source.ruleSets) ? source.ruleSets.map(item => ({
                dir: item.dir,
                file: item.file || item.dir,
                group: item.group || '',
                target: item.target || 'proxy',
                enabled: item.enabled !== false
            })) : [];
            return Object.assign({}, CONFIG_SKELETON, source, {
                base: Object.assign({}, CONFIG_SKELETON.base, source.base),
                tail: Object.assign({}, CONFIG_SKELETON.tail, source.tail),
                ruleSets,
                proxyGroupOrder: normalizeDisplayOrder(source.proxyGroupOrder, proxyGroupNames({ ruleSets, tail: source.tail })),
                customRules: Array.isArray(source.customRules) ? source.customRules.map(item => ({
                    type: item.type,
                    value: item.value,
                    target: item.target
                })) : []
            });
        },
        customRuleTarget(target) {
            return { direct: 'DIRECT', proxy: 'Proxies', reject: 'REJECT' }[target] || '';
        },
        addCustomRule() {
            const value = this.customRuleDraft.value.trim();
            if (!value || value.length > 253 || /[\s,/:#?*\\]/.test(value)) {
                this.showToastMessage('请填写域名或关键词，不要包含协议、路径、空格、逗号或通配符', 'error');
                return;
            }
            if (this.config.customRules.length >= 300) {
                this.showToastMessage('手动规则最多添加 300 条', 'error');
                return;
            }
            this.config.customRules.push(Object.assign({}, this.customRuleDraft, { value }));
            this.customRuleDraft.value = '';
            this.showToastMessage('手动规则已添加，保存后生效', 'success');
        },
        filesOf(dir) {
            const hit = this.catalogByDir[dir];
            return (hit && hit.files) || [];
        },
        isPicked(dir) {
            return !!this.pickedDirs[dir];
        },
        // proxy / direct 会生成同名策略组，其余不建组
        needGroup(target) {
            return target === 'proxy' || target === 'direct';
        },
        // 一行人话解释这条规则最终会变成什么
        describeRule(item) {
            const provider = item.dir;
            if (item.target === 'direct-only') return `RULE-SET,${provider},DIRECT`;
            if (item.target === 'reject') return `RULE-SET,${provider},AdBlock`;
            return `RULE-SET,${provider},${item.group || provider}`;
        },
        addRuleSet(item) {
            if (this.isPicked(item.dir)) return;
            this.config.ruleSets.push({
                dir: item.dir,
                file: item.defaultFile || item.dir,
                group: item.dir,
                target: 'proxy',
                enabled: true
            });
            this.showToastMessage(`已添加 ${item.dir}，排在第 ${this.ruleSets.length} 位`, 'success');
        },
        removeRuleSet(index) {
            const removed = this.config.ruleSets[index];
            this.config.ruleSets.splice(index, 1);
            this.config.proxyGroupOrder = normalizeDisplayOrder(this.config.proxyGroupOrder, proxyGroupNames(this.config));
            if (removed) this.showToastMessage(`已移除 ${removed.dir}`, 'success');
        },
        // 只保存独立展示顺序，ruleSets 始终保留原匹配优先级。
        moveProxyGroup(index, target) {
            const names = this.sortedProxyGroups.slice();
            if (target < 0 || target >= names.length || index === target) return;
            const moved = names.splice(index, 1)[0];
            names.splice(target, 0, moved);
            this.config.proxyGroupOrder = names;
        },
        // 切到不建组的模式时，把组名兜底成目录名，避免后端校验空组名
        onTargetChange(item) {
            if (!item.group) item.group = item.dir;
        },
        async saveConfig() {
            if (this.saving) return;
            this.saving = true;
            try {
                const res = await this.$axios.put('/api/rules/config', this.config);
                this.config = this.hydrateConfig(res.data);
                this.showToastMessage('分流配置已保存', 'success');
            } catch (err) {
                const msg = err.response?.data?.error || '保存失败';
                console.error('保存分流配置失败:', err);
                this.showToastMessage(msg, 'error');
            } finally {
                this.saving = false;
            }
        },
        openPreview() {
            this.previewVisible = true;
            this.fetchPreview();
        },
        async fetchPreview() {
            if (this.previewing) return;
            this.previewing = true;
            try {
                const body = this.previewUserId ? { userId: this.previewUserId } : {};
                const res = await this.$axios.post('/api/rules/preview', body);
                this.previewYaml = res.data.yaml || '';
                this.previewStats = {
                    nodeCount: res.data.nodeCount || 0,
                    groupCount: res.data.groupCount || 0,
                    providerCount: res.data.providerCount || 0,
                    ruleCount: res.data.ruleCount || 0,
                    bytes: res.data.bytes || 0
                };
            } catch (err) {
                const msg = err.response?.data?.error || '预览生成失败';
                console.error('预览合成结果失败:', err);
                this.previewYaml = '';
                this.showToastMessage(msg, 'error');
            } finally {
                this.previewing = false;
            }
        },
        async copyPreview() {
            if (!this.previewYaml) return;
            try {
                await navigator.clipboard.writeText(this.previewYaml);
                this.showToastMessage('完整 YAML 已复制', 'success');
            } catch (err) {
                this.showToastMessage('复制失败，请手动选择内容', 'error');
            }
        },
        async refreshCatalog() {
            if (this.refreshing) return;
            this.refreshing = true;
            try {
                await this.$axios.post('/api/rules/catalog/refresh');
                await this.fetchCatalog();
                this.showToastMessage(`规则目录已更新，共 ${this.catalog.total} 个目录`, 'success');
            } catch (err) {
                const msg = err.response?.data?.detail || err.response?.data?.error || '刷新规则目录失败';
                console.error('刷新规则目录失败:', err);
                this.showToastMessage(msg, 'error');
            } finally {
                this.refreshing = false;
            }
        },
        formatBytes(bytes) {
            const n = Number(bytes) || 0;
            if (n < 1024) return `${n} B`;
            if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
            return `${(n / 1024 / 1024).toFixed(2)} MB`;
        },
        showToastMessage(message, type = 'success') {
            this.toastMessage = message;
            this.toastType = type;
            this.showToast = true;
            setTimeout(() => { this.showToast = false; }, 2500);
        }
    },
    mounted() {
        // Mixin 已处理 fetchBackground() 和 triggerLoadAnimation()
        this.checkAuth();
    }
}
</script>

<style scoped>
/* ===== 基础容器 ===== */
.page-container {
    position: relative;
    min-height: 100vh;
    width: 100vw;
    overflow-x: hidden;
    font-family: var(--font-body);
    color: var(--color-ink);
}

/* ===== 粒子动画 ===== */
.particles {
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    overflow: hidden;
    z-index: 1;
    pointer-events: none;
}

.particle {
    position: absolute;
    bottom: -20px;
    background: radial-gradient(circle, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%);
    border-radius: 50%;
    animation: float-up linear infinite;
}

@keyframes float-up {
    0% { transform: translateY(0); opacity: 0; }
    10% { opacity: 1; }
    90% { opacity: 1; }
    100% { transform: translateY(-100vh); opacity: 0; }
}

/* ===== 渐变遮罩 ===== */
.gradient-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg, rgba(26, 26, 46, 0.75) 0%, rgba(22, 33, 62, 0.65) 100%);
    z-index: 2;
    pointer-events: none;
}

.content-wrapper {
    position: relative;
    z-index: 10;
    padding: var(--space-xl);
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
}

/* ===== 返回按钮 ===== */
.back-button {
    align-self: flex-start;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 10px var(--space-xl);
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-md);
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    font-size: 0.95rem;
    margin-bottom: var(--space-xl);
    transition: all var(--duration-normal) var(--ease-out);
}

.back-button:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
    color: #fff;
}

/* ===== 玻璃态卡片 ===== */
.glass-card {
    width: 100%;
    max-width: 860px;
    background: var(--color-surface-glass);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: var(--radius-2xl);
    border: 1px solid var(--color-border);
    padding: var(--space-4xl) var(--space-3xl);
    box-shadow: var(--shadow-card);
    transform: translateY(30px) scale(0.95);
    opacity: 0;
    transition: all var(--duration-slow) var(--ease-out);
}

.glass-card.show {
    transform: translateY(0) scale(1);
    opacity: 1;
}

/* ===== 页面头部 ===== */
.page-header {
    text-align: center;
    margin-bottom: var(--space-2xl);
}

.icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    background: linear-gradient(135deg, #6ee7ff 0%, #3ab7ff 100%);
    border-radius: var(--radius-xl);
    margin-bottom: var(--space-lg);
    box-shadow: 0 4px 20px rgba(110, 231, 255, 0.4);
}

.title {
    font-size: 2rem;
    font-weight: 700;
    background: linear-gradient(90deg, #6ee7ff, #9ef3ff, #ffd166, #3ab7ff);
    background-size: 300% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: gradient-flow 4s ease infinite;
    margin-bottom: var(--space-sm);
}

@keyframes gradient-flow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
}

.subtitle {
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.95rem;
}

/* ===== 分隔线 ===== */
.divider {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    margin: var(--space-2xl) 0;
}

.divider-line {
    height: 1px;
    width: 60px;
    background: linear-gradient(90deg, transparent, rgba(110, 231, 255, 0.5), transparent);
}

.divider-dot {
    width: 6px;
    height: 6px;
    background: linear-gradient(135deg, #6ee7ff, #ffd166);
    border-radius: 50%;
}

/* ===== 未登录 ===== */
.auth-required {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    padding: var(--space-4xl) 0;
    text-align: center;
}

.auth-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
}

.auth-desc {
    font-size: 0.88rem;
    color: var(--color-subtle);
}

.auth-btn {
    margin-top: var(--space-sm);
    text-decoration: none;
    display: inline-block;
}

/* ===== 区块 ===== */
.section-block {
    margin-bottom: var(--space-2xl);
    padding-top: var(--space-lg);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-md);
    flex-wrap: wrap;
    margin-bottom: var(--space-md);
}

.section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.92rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
}

.section-note {
    font-size: 0.74rem;
    color: rgba(255, 255, 255, 0.4);
}

.sub-label {
    margin: var(--space-lg) 0 var(--space-sm);
    font-size: 0.82rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.6);
}

.count-badge {
    padding: 2px 9px;
    border-radius: var(--radius-pill);
    background: rgba(110, 231, 255, 0.12);
    border: 1px solid rgba(110, 231, 255, 0.2);
    color: rgba(110, 231, 255, 0.85);
    font-size: 0.72rem;
    font-family: var(--font-mono);
}

/* ===== 优先级提示 ===== */
.priority-tip {
    display: flex;
    align-items: flex-start;
    gap: var(--space-sm);
    padding: 10px var(--space-md);
    margin-bottom: var(--space-md);
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.2);
    border-radius: var(--radius-md);
    color: rgba(255, 209, 102, 0.9);
    font-size: 0.78rem;
    line-height: 1.5;
}

/* ===== 手动规则 ===== */
.custom-rule-tip {
    color: var(--color-muted);
    font-size: 0.75rem;
    line-height: 1.7;
    margin: 10px 0 14px;
}

.custom-rule-form,
.custom-rule-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 10px;
}

.custom-rule-form {
    padding: var(--space-md);
    background: rgba(110, 231, 255, 0.04);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
}

.custom-field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
}

.custom-value {
    flex: 1 1 200px;
}

.custom-value .mini-input {
    width: 100%;
    box-sizing: border-box;
}

.custom-rule-fields {
    margin-bottom: 8px;
}

.custom-rule-fields .mini-input {
    flex: 1 1 160px;
    min-width: 0;
}

/* ===== 已选规则集列表 ===== */
.rule-list {
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
}

.rule-row {
    display: flex;
    align-items: flex-start;
    gap: var(--space-md);
    padding: var(--space-md);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-md);
    transition: all var(--duration-normal) var(--ease-out);
}

.rule-row:hover {
    border-color: rgba(110, 231, 255, 0.3);
}

.rule-row.row-off {
    opacity: 0.5;
}

.rule-index {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    background: rgba(110, 231, 255, 0.12);
    color: rgba(110, 231, 255, 0.85);
    font-family: var(--font-mono);
    font-size: 0.74rem;
    font-weight: 600;
}

.rule-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.rule-dir-line {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    flex-wrap: wrap;
}

.rule-dir {
    font-size: 0.94rem;
    font-weight: 600;
    color: #fff;
}

.rule-file {
    font-family: var(--font-mono);
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.4);
}

.rule-fields {
    display: flex;
    gap: var(--space-sm);
    flex-wrap: wrap;
}

.rule-preview-line {
    font-family: var(--font-mono);
    font-size: 0.7rem;
    color: rgba(110, 231, 255, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.rule-ops {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}

.icon-btn {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: var(--radius-sm);
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
    transition: all var(--duration-fast) var(--ease-out);
}

.icon-btn:hover:not(:disabled) {
    background: rgba(110, 231, 255, 0.16);
    border-color: rgba(110, 231, 255, 0.3);
    color: #6ee7ff;
}

.icon-btn:disabled {
    opacity: 0.25;
    cursor: not-allowed;
}

.icon-btn.icon-danger:hover:not(:disabled) {
    background: rgba(244, 67, 54, 0.18);
    border-color: rgba(244, 67, 54, 0.3);
    color: #e57373;
}

/* ===== 小号表单控件 ===== */
.field-inline {
    display: flex;
    align-items: center;
    gap: 6px;
}

.field-tag {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.5);
    white-space: nowrap;
}

.mini-input,
.mini-select {
    padding: 5px 9px;
    background: rgba(0, 0, 0, 0.22);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: var(--radius-sm);
    color: #fff;
    font-size: 0.78rem;
    outline: none;
    transition: all var(--duration-fast) var(--ease-out);
}

.mini-input {
    width: 130px;
}

.mini-input:focus,
.mini-select:focus {
    border-color: rgba(110, 231, 255, 0.5);
}

.mini-input:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.mini-select option {
    background: #16213e;
    color: #fff;
}

.file-select {
    max-width: 190px;
    font-family: var(--font-mono);
    font-size: 0.7rem;
}

/* ===== 目录工具条 ===== */
.catalog-toolbar {
    display: flex;
    align-items: center;
    gap: var(--space-md);
    margin-bottom: var(--space-md);
    flex-wrap: wrap;
}

.search-box {
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    padding: 9px var(--space-md);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-md);
    transition: all var(--duration-normal) var(--ease-out);
}

.search-box:focus-within {
    border-color: rgba(110, 231, 255, 0.5);
    box-shadow: 0 0 12px rgba(110, 231, 255, 0.12);
}

.search-input {
    flex: 1;
    min-width: 0;
    background: transparent;
    border: 0;
    outline: none;
    color: #fff;
    font-size: 0.88rem;
}

.search-input::placeholder {
    color: rgba(255, 255, 255, 0.35);
}

.search-clear {
    background: transparent;
    border: 0;
    color: rgba(255, 255, 255, 0.45);
    font-size: 1.05rem;
    line-height: 1;
    cursor: pointer;
}

.search-clear:hover {
    color: #fff;
}

/* ===== 开关 ===== */
.switch-line {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    cursor: pointer;
    user-select: none;
}

.switch-line input {
    display: none;
}

.switch-track {
    position: relative;
    width: 36px;
    height: 20px;
    flex-shrink: 0;
    border-radius: var(--radius-pill);
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.15);
    transition: all var(--duration-normal) var(--ease-out);
}

.switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.7);
    transition: all var(--duration-normal) var(--ease-out);
}

.switch-line input:checked + .switch-track {
    background: rgba(110, 231, 255, 0.35);
    border-color: rgba(110, 231, 255, 0.5);
}

.switch-line input:checked + .switch-track .switch-thumb {
    left: 18px;
    background: #6ee7ff;
}

.switch-text {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.72);
}

/* ===== 目录列表 ===== */
.catalog-list {
    max-height: 380px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-right: 4px;
}

.catalog-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-sm);
    padding: 8px var(--space-md);
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: var(--radius-sm);
}

.catalog-row:hover {
    background: rgba(110, 231, 255, 0.06);
}

.catalog-row.is-picked {
    opacity: 0.42;
}

.catalog-info {
    display: flex;
    align-items: center;
    gap: var(--space-sm);
    min-width: 0;
}

.catalog-dir {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.86);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.behavior-tag {
    padding: 1px 7px;
    border-radius: 4px;
    font-family: var(--font-mono);
    font-size: 0.66rem;
    border: 1px solid;
}

.behavior-classical {
    background: rgba(110, 231, 255, 0.1);
    border-color: rgba(110, 231, 255, 0.22);
    color: rgba(110, 231, 255, 0.8);
}

.behavior-domain {
    background: rgba(76, 175, 80, 0.1);
    border-color: rgba(76, 175, 80, 0.25);
    color: #81c784;
}

.behavior-ipcidr {
    background: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.25);
    color: #ffd166;
}

.files-tag {
    font-size: 0.66rem;
    color: rgba(255, 255, 255, 0.35);
    white-space: nowrap;
}

.pick-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    background: rgba(110, 231, 255, 0.12);
    border: 1px solid rgba(110, 231, 255, 0.26);
    border-radius: 6px;
    color: #6ee7ff;
    font-size: 0.74rem;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    transition: all var(--duration-fast) var(--ease-out);
}

.pick-btn:hover:not(:disabled) {
    background: rgba(110, 231, 255, 0.24);
}

.pick-btn:disabled {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.4);
    cursor: not-allowed;
}

.catalog-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: var(--space-sm);
    padding: 8px var(--space-md);
    background: rgba(0, 0, 0, 0.18);
    border-radius: var(--radius-sm);
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.74rem;
}

/* ===== 基础设置 ===== */
.settings-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--space-md);
}

.field-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-block.full {
    grid-column: 1 / -1;
}

.field-hint {
    font-size: 0.7rem;
    color: rgba(255, 255, 255, 0.35);
}

.input-field {
    width: 100%;
    box-sizing: border-box;
    padding: 10px var(--space-lg);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-md);
    color: #fff;
    font-size: 0.9rem;
    outline: none;
    transition: all var(--duration-normal) var(--ease-out);
}

.input-field.mono {
    font-family: var(--font-mono);
    font-size: 0.82rem;
}

.input-field::placeholder {
    color: rgba(255, 255, 255, 0.4);
}

.input-field:focus {
    border-color: rgba(110, 231, 255, 0.6);
    box-shadow: 0 0 12px rgba(110, 231, 255, 0.15);
}

.seg-group {
    display: flex;
    gap: 6px;
}

.seg-item {
    flex: 1;
    padding: 9px 10px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: var(--radius-md);
    color: rgba(255, 255, 255, 0.6);
    font-family: var(--font-mono);
    font-size: 0.78rem;
    cursor: pointer;
    transition: all var(--duration-fast) var(--ease-out);
}

.seg-item.active {
    background: rgba(110, 231, 255, 0.18);
    border-color: rgba(110, 231, 255, 0.42);
    color: #6ee7ff;
}

.toggle-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: var(--space-md);
    margin-top: var(--space-md);
    padding: var(--space-md);
    background: rgba(0, 0, 0, 0.18);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: var(--radius-md);
}

.final-target {
    justify-content: flex-start;
}

/* ===== 底部操作条 ===== */
.action-bar {
    display: flex;
    gap: var(--space-md);
    flex-wrap: wrap;
    padding-top: var(--space-lg);
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px var(--space-2xl);
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.3), rgba(58, 183, 255, 0.3));
    border: 1px solid rgba(110, 231, 255, 0.4);
    border-radius: var(--radius-md);
    color: #fff;
    font-size: 0.92rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--duration-normal) var(--ease-out);
    white-space: nowrap;
}

.add-btn:hover:not(:disabled) {
    background: linear-gradient(135deg, rgba(110, 231, 255, 0.5), rgba(58, 183, 255, 0.5));
    transform: translateY(-2px);
    box-shadow: var(--shadow-hover);
}

.add-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.ghost-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px var(--space-xl);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: var(--radius-md);
    color: rgba(255, 255, 255, 0.78);
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all var(--duration-normal) var(--ease-out);
    white-space: nowrap;
}

.ghost-btn:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.ghost-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.spinning {
    animation: spin 1s linear infinite;
}

/* ===== 加载 / 空状态 ===== */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-lg);
    padding: var(--space-4xl) 0;
    color: rgba(255, 255, 255, 0.6);
}

.spinner {
    width: 36px;
    height: 36px;
    border: 3px solid rgba(110, 231, 255, 0.2);
    border-top-color: #6ee7ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

.spinner-sm {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(110, 231, 255, 0.2);
    border-top-color: #6ee7ff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.logs-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-sm);
    padding: var(--space-lg);
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.85rem;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-md);
    text-align: center;
    padding: var(--space-3xl) 0;
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.88rem;
}

/* ===== 底部提示 ===== */
.footer-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-sm);
    margin-top: var(--space-2xl);
    padding: var(--space-md);
    background: rgba(255, 193, 7, 0.1);
    border: 1px solid rgba(255, 193, 7, 0.2);
    border-radius: var(--radius-md);
    color: rgba(255, 193, 7, 0.9);
    font-size: 0.86rem;
    text-align: center;
}

/* ===== 预览弹层 ===== */
.modal-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-xl);
    z-index: 1001;
}

.preview-modal {
    width: 100%;
    max-width: 760px;
    max-height: 86vh;
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    padding: var(--space-2xl);
    background: linear-gradient(135deg, rgba(16, 24, 40, 0.96), rgba(20, 30, 52, 0.94));
    border: 1px solid rgba(110, 231, 255, 0.25);
    border-radius: var(--radius-xl);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.preview-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--space-md);
}

.preview-title {
    font-size: 1rem;
    font-weight: 700;
    color: #fff;
}

.preview-subtitle {
    margin-top: 4px;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.45);
}

.preview-close {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--radius-sm);
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.66);
    font-size: 1.2rem;
    line-height: 1;
    cursor: pointer;
}

.preview-close:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
}

.preview-stats {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.stat-chip {
    padding: 3px 10px;
    border-radius: var(--radius-pill);
    background: rgba(110, 231, 255, 0.1);
    border: 1px solid rgba(110, 231, 255, 0.2);
    color: rgba(110, 231, 255, 0.85);
    font-size: 0.74rem;
    font-family: var(--font-mono);
}

.preview-pre {
    flex: 1;
    min-height: 200px;
    overflow: auto;
    margin: 0;
    padding: var(--space-md);
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: var(--radius-md);
    color: rgba(255, 255, 255, 0.75);
    font-family: var(--font-mono);
    font-size: 0.72rem;
    line-height: 1.5;
    white-space: pre;
}

.preview-more {
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.4);
}

.preview-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-md);
    flex-wrap: wrap;
}

/* ===== Toast ===== */
.toast {
    position: fixed;
    top: 30px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px var(--space-2xl);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(20px);
    border-radius: 14px;
    color: #fff;
    font-size: 1rem;
    z-index: 1002;
    box-shadow: var(--shadow-card);
}

.toast.success {
    border: 1px solid rgba(76, 175, 80, 0.5);
}

.toast.error {
    border: 1px solid rgba(244, 67, 54, 0.5);
}

.toast-enter-active, .toast-leave-active {
    transition: all 0.4s var(--ease-out);
}

.toast-enter, .toast-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
}

.modal-enter-active, .modal-leave-active {
    transition: opacity var(--duration-normal) ease;
}

.modal-enter-active .preview-modal, .modal-leave-active .preview-modal {
    transition: transform var(--duration-normal) ease;
}

.modal-enter, .modal-leave-to {
    opacity: 0;
}

.modal-enter .preview-modal {
    transform: scale(0.94) translateY(20px);
}

/* ===== 响应式 ===== */
@media (max-width: 640px) {
    .glass-card {
        padding: var(--space-2xl) var(--space-lg);
        border-radius: var(--radius-xl);
    }

    .title {
        font-size: 1.6rem;
    }

    .rule-row {
        flex-wrap: wrap;
    }

    .rule-ops {
        width: 100%;
        justify-content: flex-end;
    }

    .mini-input {
        width: 110px;
    }

    .action-bar .add-btn,
    .action-bar .ghost-btn {
        flex: 1;
    }
}

@media (min-width: 1024px) {
    .glass-card {
        max-width: 960px;
        padding: var(--space-5xl) var(--space-4xl);
    }
}
</style>
